// Local Mock server bridge — wraps the Rust `mock_*` Tauri commands and builds
// the dataset payload from a workspace snapshot.
//
// The Rust server (src-tauri/src/mock_server.rs) serves saved response examples
// over http://127.0.0.1:{port}/mock/{collectionId}/{path}. It is fully offline
// and available to all plan tiers. Mock examples live on each request under
// `request.mock.responseExamples` and never leave the device (they are not
// included in the cloud publish payload).

import { invoke, isTauri as coreIsTauri } from '@tauri-apps/api/core';
import { findRequestTabForRequest } from '../utils/requestResolve';
import { schemasWithTabOverlay } from '../utils/schemaTree';
import { normalizeMock } from '../utils/workspaceTabPersistence';

function isTauri() {
  try {
    return coreIsTauri();
  } catch {
    return false;
  }
}

const DEFAULT_MOCK_PORT = 8780;

/**
 * Start the local mock server with the given dataset.
 * @returns {Promise<MockStatus>}
 */
export async function startMockServer(dataset, preferredPort = DEFAULT_MOCK_PORT) {
  if (!isTauri()) throw new Error('Local Mock server requires the desktop app');
  return invoke('mock_start', { dataset, preferredPort });
}

export async function stopMockServer() {
  if (!isTauri()) return;
  return invoke('mock_stop');
}

/** @returns {Promise<MockStatus>} */
export async function getMockStatus() {
  if (!isTauri()) {
    return { running: false, port: null, baseUrl: null, routeCount: 0 };
  }
  return invoke('mock_status');
}

/** Hot-swap the dataset on a running server (no restart). */
export async function reloadMockServer(dataset) {
  if (!isTauri()) return;
  return invoke('mock_reload', { dataset });
}

/**
 * Extract the route path (pathname only, no scheme/host/query) from a request
 * URL. Matching is by literal path, so {{vars}} are left as written.
 */
export function mockPathFromUrl(rawUrl) {
  const raw = (rawUrl ?? '').trim();
  if (!raw) return '';
  // Strip query/hash first. A LEADING {{var}} stands for scheme + host
  // ({{baseUrl}}/users) — it is not part of the path (card e66fbe9b).
  const noQuery = raw.split('?')[0].split('#')[0].replace(/^\{\{[^{}]+\}\}/, '') || '/';
  try {
    const u = new URL(noQuery);
    return u.pathname.replace(/%7B/gi, '{').replace(/%7D/gi, '}') || '/';
  } catch {
    // Not an absolute URL — strip a leading scheme+host if present, else treat
    // the whole thing as a path.
    const m = noQuery.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/]*(\/.*)?$/);
    if (m) return m[1] || '/';
    return noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
  }
}

/** Build the per-request mock URL shown in the Mock tab. */
export function buildMockUrl(baseUrl, collectionId, path) {
  if (!baseUrl || !collectionId) return '';
  const p = path && path.startsWith('/') ? path : `/${path || ''}`;
  return `${baseUrl}/mock/${collectionId}${p}`;
}

function effectiveRequestMock(request, tab) {
  if (!tab) return normalizeMock(request?.mock);
  const preferTab = Boolean(tab.hasUnsavedChanges) || hasMockExamples(tab.mock);
  if (preferTab && hasMockExamples(tab.mock)) return normalizeMock(tab.mock);
  if (hasMockExamples(request?.mock)) return normalizeMock(request.mock);
  if (hasMockExamples(tab.mock)) return normalizeMock(tab.mock);
  return normalizeMock(request?.mock ?? tab.mock);
}

function hasMockExamples(mock) {
  return Array.isArray(mock?.responseExamples) && mock.responseExamples.length > 0;
}

/**
 * Overlay open request tabs onto collection requests so unsaved mock edits are served.
 */
export function collectionsWithTabMockOverlay(collections, requestTabs = []) {
  if (!requestTabs?.length) return collections || [];
  return (collections || []).map((collection) => ({
    ...collection,
    requests: (collection.requests || []).map((request) => {
      const tab = findRequestTabForRequest(requestTabs, request.id);
      if (!tab) return request;
      const mock = effectiveRequestMock(request, tab);
      if (!hasMockExamples(mock)) return request;
      return { ...request, mock };
    }),
  }));
}

/**
 * Serialize a workspace snapshot into the Rust `MockDataset` payload.
 * Only requests with a usable path and at least the `mock` slot are included;
 * requests with no examples still produce a route (the server returns a fixed
 * fallback for those).
 *
 * @param {object} snapshot - `{ collections, schemas? }` or full workspace snapshot
 * @param {Array} [requestTabs] - open tabs; unsaved mock edits overlay collections
 * @param {Array} [schemaTabs] - open schema tabs; unsaved schema edits overlay schemas
 */
export function buildMockDataset(snapshot, requestTabs = [], schemaTabs = []) {
  const schemas = schemasWithTabOverlay(snapshot?.schemas ?? null, schemaTabs);
  const collections = collectionsWithTabMockOverlay(
    snapshot?.collections || [],
    requestTabs
  );
  const routes = [];
  for (const collection of collections) {
    const collectionId = String(collection?.id ?? '');
    if (!collectionId) continue;
    for (const request of collection?.requests || []) {
      const path = mockPathFromUrl(request?.url);
      if (!path) continue;
      const examples = request?.mock?.responseExamples || [];
      routes.push({
        requestId: String(request?.id ?? ''),
        collectionId,
        method: (request?.method || 'GET').toUpperCase(),
        path,
        examples: examples.map((ex) => serializeExample(ex)),
      });
    }
  }
  return { routes, schemas: serializeSchemasForWire(schemas) };
}

function serializeSchemasForWire(schemas) {
  const items = (schemas?.items || []).map((item) => ({
    id: String(item.id ?? ''),
    rootType: item.rootType === 'array' ? 'array' : 'object',
    nodes: (item.nodes || []).map(serializeSchemaNode),
  }));
  return { items };
}

function serializeSchemaNode(node) {
  const out = {
    name: node?.name ?? '',
    type: node?.type ?? 'string',
    value: node?.value ?? '',
    refSchemaId: node?.refSchemaId ?? null,
    children: (node?.children || []).map(serializeSchemaNode),
  };
  if (Array.isArray(node?.enum) && node.enum.length > 0) {
    out.enum = node.enum.map(String);
  }
  if (typeof node?.minItems === 'number') out.minItems = node.minItems;
  if (typeof node?.maxItems === 'number') out.maxItems = node.maxItems;
  return out;
}

function serializeExample(ex) {
  const fromSchema = ex?.sourceType === 'schema';
  return {
    id: ex?.id ?? '',
    name: ex?.name ?? null,
    status: Number(ex?.status) || 200,
    headers: toKeyValueList(ex?.headers),
    body: fromSchema ? '' : (ex?.body ?? ''),
    contentType: fromSchema ? (ex?.contentType || 'application/json') : (ex?.contentType ?? null),
    matchQuery: toKeyValueList(ex?.matchQuery),
    matchHeaders: toKeyValueList(ex?.matchHeaders),
    enabled: ex?.enabled !== false,
    sourceType: fromSchema ? 'schema' : 'example',
    schemaId: fromSchema ? (ex?.schemaId ?? null) : null,
  };
}

function toKeyValueList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((kv) => kv && kv.key)
    .map((kv) => ({ key: String(kv.key), value: String(kv.value ?? '') }));
}
